'use client'
import React, {useRef} from 'react'
import {HgHeading} from '~/design-system/hg/components'
import {cn} from '~/design-system/utils'
import {Disclaimer} from './_components/Disclaimer'
import CinematicHero from './_components/CinematicHero'
import {DefaultInnerHero} from './_components/DefaultInnerHero'
import {SplitImageInnerHero} from './_components/SplitImageInnerHero'
import {getHeadingContentTypeProps, hgHeroVariants} from './utils'
import {grid, gridItem} from '~/design-system/foundations'
import {type HgHeroProps} from './types'

export default function HgHero(props: HgHeroProps) {
  const {disclaimer, className, alignment} = props
  const containerRef = useRef<HTMLDivElement>(null)
  const headingContainerRef = useRef<HTMLDivElement>(null)
  const isSplitImage = props.type === 'splitImage'
  const isCinematic = props.type === 'cinematic'

  const {heading, headingContainer, gridSlot} = hgHeroVariants({
    type: props.type,
    size: isSplitImage ? props.size : undefined,
    alignment: alignment || 'center',
  })

  // Since these two components are similar
  // we can render them conditionally inside the hero.
  const renderInnerHeroContent = () => {
    switch (props.type) {
      case 'default':
        return (
          <DefaultInnerHero
            {...props}
            gridSlot={gridSlot}
            headingContainer={headingContainer}
            headingContainerRef={headingContainerRef}
            heading={heading}
          />
        )
      case 'splitImage':
        return (
          <SplitImageInnerHero
            {...props}
            classNames={{
              gridSlot: gridSlot(),
              headingContainer: headingContainer(),
              heading: heading(),
            }}
            headingContainerRef={headingContainerRef}
            containerRef={containerRef}
          />
        )
      case 'headingOnly':
      default:
        return (
          <div
            className={grid({
              className: gridSlot(),
            })}
          >
            <div
              className={gridItem({
                size: 'heading',
                className: headingContainer(),
              })}
              ref={headingContainerRef}
            >
              <HgHeading
                {...getHeadingContentTypeProps({
                  props,
                  buttonVariant: 'tonal',
                  alwaysStackedEmail: false,
                })}
                variation="hero"
                topSpacing="s8"
                className={heading()}
                headline={props.headline}
                subhead={props.subhead}
                alignment={props.alignment || 'center'}
                breadcrumbs={props.breadcrumbs}
              />
            </div>
          </div>
        )
    }
  }

  // Cinematic hero is a special case because it changes the layout,
  // and a few other elements so we render it separately.
  if (isCinematic) return <CinematicHero {...props} />

  return (
    <div className={cn('group grid h-fit w-full', className)} ref={containerRef}>
      {renderInnerHeroContent()}
      {disclaimer && <Disclaimer disclaimer={disclaimer} />}
    </div>
  )
}
